import { useQuery } from "@apollo/client";
import { Stack, TableCell, TableRow, Typography, useTheme } from "@mui/material";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useState } from "react";

import { DiscrepancyEventHistory } from "./DiscrepancyEventHistory";
import { DISCREPANCY_EVENTS_QUERY } from "./discrepancyEventsQuery";
import {
  type DiscrepancyStatus,
  getStatusChipTooltip,
  getUndoResolutionEligibility,
} from "./DiscrepancyReviewDialog.helpers";
import { EXPANDER_INSET_WITH_SELECTION } from "./discrepancyTableSx";
import { RowActionButton } from "./RowActionButton";

/**
 * The chrome both row expanders had byte-for-byte: the cell, the status
 * explanation, the three staff actions with their refusal copy, and the event
 * history. Only the middle — which values and verdicts a finding compares —
 * differs between the two, and that arrives as `children`.
 *
 * Shared for the same reason `DiscrepancyEventHistory` is: the two panes cannot
 * drift on the button copy, the padding, or the type scale.
 */

export interface DiscrepancyRowExpanderShellProps {
  /** Drives the status explanation, the actions' eligibility, and the history query. */
  discrepancy: {
    id: string;
    processedManifestId: string;
    field: string;
    kind?: string | null;
    resolutionPath?: string | null;
    resolvedBy?: unknown;
    escalation?: string | null;
    status?: string | null;
  };
  status: DiscrepancyStatus;
  open: boolean;
  colSpan: number;
  /** Every action here is staff-only; a customer still gets values and history. */
  isStaff: boolean;
  /** False on the Resolve button when the finding has no resolvable action left. */
  canResolve: boolean;
  /** True while any resolve mutation is in flight, which blocks all three actions. */
  isMutating: boolean;
  onResolve: () => void;
  onMarkUnresolved: () => void;
  onUndoResolution: () => void;
  /** The finding's own values and verdicts. */
  children: React.ReactNode;
}

export function DiscrepancyRowExpanderShell({
  discrepancy,
  status,
  open,
  colSpan,
  isStaff,
  canResolve,
  isMutating,
  onResolve,
  onMarkUnresolved,
  onUndoResolution,
  children,
}: DiscrepancyRowExpanderShellProps) {
  const theme = useTheme();
  const [confirmUndoOpen, setConfirmUndoOpen] = useState(false);
  const isPending = discrepancy.status === "PENDING";
  const statusExplanation = getStatusChipTooltip(discrepancy, status);

  const { data, error } = useQuery(DISCREPANCY_EVENTS_QUERY, {
    variables: { processedManifestId: discrepancy.processedManifestId },
    skip: !open,
    /*
     * Revalidate on every expand, not just the first.
     *
     * The mutations name this query in `refetchQueries`, which only reaches
     * queries Apollo is actively watching. This one is skipped while the row
     * is collapsed, so resolving from the section's bulk-action bar — which
     * needs no row open — refetches nothing. At `cache-first` the next expand
     * then served a cached history missing the event just written.
     */
    fetchPolicy: "cache-and-network",
  });

  // Gated on having data rather than on `loading`: `cache-and-network` keeps
  // `loading` true while it revalidates a manifest already in the cache, so a
  // re-expand would otherwise read as "still loading" over history it could
  // render and an Undo it could already offer.
  const events = data?.manifestDiscrepancies.find((d) => d.id === discrepancy.id)?.events;
  const undo = getUndoResolutionEligibility(discrepancy, events);
  /*
   * The button appears on the two statuses an undo can leave. On every other
   * status the row was never closed, so offering it there would explain
   * something the reader is not attempting. On those two statuses it greys
   * rather than disappearing whenever undo is refused (an e-Manifest
   * correction request, a resolution that wrote nothing locally, history
   * still loading), because the reason is worth reading.
   */
  const isIgnored = discrepancy.status === "IGNORED";
  const showUndo = discrepancy.status === "RESOLVED" || isIgnored;
  const undoLabel = isIgnored ? "Undo non-discrepancy" : "Undo resolution";

  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        sx={{
          pt: 2,
          pb: 2.5,
          pr: 2,
          // Lines the detail up with the row's first text column, so nothing
          // in it sits left of "Line 5". Anything less and the block reads as
          // belonging to the chevron and checkbox rather than to the row.
          pl: EXPANDER_INSET_WITH_SELECTION,
          // No fill. The rules carry the boundary on their own: the summary row
          // above drops its own rule while open, so the row and this detail read
          // as one block, and this rule closes it. A wash here only added a
          // second, weaker edge saying the same thing.
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack spacing={2}>
          {statusExplanation && (
            <Typography variant="body2" color="text.secondary">
              {statusExplanation}
            </Typography>
          )}

          {children}

          {isStaff && (
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Only what applies to this row's state. Resolve and Reopen used
                  to render together, greyed, which put two contradictory
                  actions side by side and left the reader to work out which
                  one their row was in. Undo keeps its greyed form: its reason
                  says why a specific resolution cannot be undone, which is
                  worth reading. */}
              {canResolve && (
                <RowActionButton blocked={isMutating} reason="" onClick={onResolve}>
                  Resolve
                </RowActionButton>
              )}
              {isPending && (
                <RowActionButton blocked={isMutating} reason="" onClick={onMarkUnresolved}>
                  Reopen
                </RowActionButton>
              )}
              {/* Undo reopens the row, and on a vendor correction writes the
                  prior value back, so the click opens a confirmation rather
                  than acting. Its tooltip carries what it will do, or why it
                  cannot — the other two buttons have nothing to explain. */}
              {showUndo && (
                <RowActionButton
                  blocked={isMutating || !undo.enabled}
                  reason={undo.reason}
                  onClick={() => setConfirmUndoOpen(true)}
                >
                  {undoLabel}
                </RowActionButton>
              )}
            </Stack>
          )}

          <DiscrepancyEventHistory events={events} error={error != null} />

          <ConfirmDialog
            open={confirmUndoOpen}
            header={isIgnored ? "Undo this non-discrepancy?" : "Undo this resolution?"}
            info={
              isIgnored
                ? "The finding returns to unresolved and needs a decision again. The event history keeps both entries."
                : "The vendor value recorded before the resolution is written back and the discrepancy reopens. The event history keeps both entries."
            }
            // Short, and different from the row button's own label so the two
            // are distinguishable while the dialog is open.
            confirmText="Undo"
            loading={isMutating}
            onClose={() => setConfirmUndoOpen(false)}
            onConfirm={() => {
              setConfirmUndoOpen(false);
              onUndoResolution();
            }}
          />
        </Stack>
      </TableCell>
    </TableRow>
  );
}
